/**
 * The events grid piece (the events page): the breadcrumb, the heading and
 * every upcoming event by date in a grid, with Load more while the studio has
 * more. Says so in one line when the studio lists no upcoming event.
 */
import type { SiteEventSummaryDto } from '@telify/shared';

import { definePiece } from '../../contract/define';
import { eventCardParts, eventsMineOption, openEventBooking, type MineMark } from '../events-row/contract';

export const contract = definePiece({
  name: 'events-grid',
  description: 'Every upcoming event by date in a grid, four across at desktop width, with Load more while there are more.',
  placeable: true,
  options: {
    showPhoto: { type: 'boolean', default: true, description: "Show each event's main photo.", showsPart: 'photo' },
    showSummary: { type: 'boolean', default: true, description: "Show each event's short line.", showsPart: 'summary' },
    heading: { type: 'string', default: 'Events', maxLength: 80, description: 'The page heading; null for none.' },
    emptyText: {
      type: 'string',
      default: 'There are no upcoming events right now.',
      maxLength: 200,
      description: 'The line shown instead of the grid when there is no upcoming event.',
    },
    mine: eventsMineOption,
  },
  parts: {
    section: { description: 'The whole page section.', required: false },
    breadcrumb: { description: 'The trail: the studio name, then Events.', required: false },
    'breadcrumb-home': { description: "The studio's name, linking to the home page.", required: false },
    'breadcrumb-separator': { description: 'The slash between two steps of the trail.', required: false },
    'breadcrumb-here': { description: 'Events, the page the visitor is on.', required: false },
    head: { description: 'The block holding the heading.', required: false },
    heading: { description: 'The page heading.', required: false, role: 'title' },
    list: { description: 'The grid of events.', required: true },
    empty: { description: 'The one line shown when there is no upcoming event.', required: false },
    more: { description: 'The line under the grid holding Load more.', required: false },
    'load-more': { description: 'Load more, while the studio has more events.', required: false, states: ['disabled'] },
    ...eventCardParts,
  },
  actions: {
    open: { description: "Opens an event's page.", args: [{ name: 'eventId', kind: 'item', list: 'events', key: 'id' }] },
    loadMore: { description: 'Loads the next events.', args: [] },
    home: { description: 'Opens the home page.', args: [] },
    openBooking: openEventBooking,
  },
  events: {
    opened: 'A visitor opened an event.',
  },
});

/** What the events grid knows. */
export interface EventsGridState {
  /** loading until the first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** Every upcoming event loaded so far, by start time; each carries its `spotsLeft`. */
  readonly events: readonly SiteEventSummaryDto[];
  /** The studio has more events than loaded (Load more shows). */
  readonly hasMore: boolean;
  /** The next events are on their way. */
  readonly loadingMore: boolean;
  /** The studio's name, for the breadcrumb. */
  readonly studioName: string | null;
  /** The visitor's IANA time zone; dates and times are drawn in it. */
  readonly timezone: string;
  /** With the mine option, the signed-in client's own booking of each event they have booked, by the event's id; empty for a guest. */
  readonly marks: Readonly<Record<string, MineMark>>;
}

/** What a drawing can ask the events grid to do. */
export interface EventsGridActions {
  /** Open the page of the event with this id. */
  readonly open: (eventId: string) => void;
  /** Load the next events; does nothing when there are none or they are on their way. */
  readonly loadMore: () => void;
  /** Open the home page. */
  readonly home: () => void;
  /** Open the client's own booking of the event with this id: the step it waits on, else its page. An event with no mark does nothing. */
  readonly openBooking: (eventId: string) => void;
}

/** What the events grid tells its host. */
export interface EventsGridEvents {
  /** A visitor opened an event; carries its id. */
  readonly opened: { readonly eventId: string };
}
